<template>
    <v-card class="product-card" elevation="0" :to="{ name: 'product_detail', params: { id: product.id } }">

        <!-- Image -->
        <div class="img-wrap">
            <v-img
                :src="getProductImage(product)"
                aspect-ratio="1"
                contain
                class="product-img"
            >
                <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate size="22" width="2" color="#e5e7eb" />
                    </v-row>
                </template>
            </v-img>
        </div>

        <!-- Info -->
        <div class="card-info">

            <!-- Name -->
            <p class="p-name">{{ product.productName || product.name || product.title }}</p>

            <!-- Rating -->
            <div v-if="product._reviewAvg" class="p-rating">
                <v-icon size="11" color="#FBBF24">mdi-star</v-icon>
                <span class="r-val">{{ Number(product._reviewAvg).toFixed(1) }}</span>
                <span class="r-cnt">({{ product._reviewCount || 0 }})</span>
            </div>

            <!-- Spacer -->
            <div class="grow"></div>

            <!-- Price row -->
            <div class="price-row">
                <span class="p-price">฿{{ formatPrice(product.price) }}</span>
                <div v-if="product.viewCount" class="p-views">
                    <v-icon size="11" color="#c4c4c4">mdi-eye-outline</v-icon>
                    <span>{{ formatViews(product.viewCount) }}</span>
                </div>
            </div>

            <!-- Location -->
            <div v-if="shopLocation" class="p-location">
                <v-icon size="10" color="#c4c4c4">mdi-map-marker-outline</v-icon>
                <span>{{ shopLocation }}</span>
            </div>

        </div>
    </v-card>
</template>

<script>
import { getAddress } from "@/utils/address";
import { getProductImage, getImageUrl } from "@/utils/image";

export default {
    name: "ProductCard",
    props: {
        product: { type: Object, required: true },
    },
    computed: {
        shopLocation() {
            const seller = this.product.seller || this.product.shop;
            if (!seller) return "";
            const addr = getAddress(seller);
            return addr.province?.nameTh ? `จ.${addr.province.nameTh}` : "";
        },
    },
    methods: {
        formatViews(n) {
            if (!n) return '0';
            if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
            if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
            return String(n);
        },
        formatPrice(price) {
            if (!price && price !== 0) return "0.00";
            return parseFloat(price).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        getProductImage,
        getImageUrl,
    },
};
</script>

<style scoped>
/* ── Card shell ─────────────────────────────── */
.product-card {
    border-radius: 16px !important;
    background: #fff !important;
    box-shadow: 0 1px 4px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.06) !important;
    transition: transform .2s ease, box-shadow .2s ease;
    cursor: pointer;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    text-decoration: none;
}
.product-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0,0,0,.1), 0 12px 32px rgba(0,0,0,.09) !important;
}

/* ── Image ──────────────────────────────────── */
.img-wrap {
    background: #f6f6f6;
    padding: 10px;
    border-bottom: 1px solid #f0f0f0;
}
.product-img {
    border-radius: 8px;
}

/* ── Content ────────────────────────────────── */
.card-info {
    padding: 10px 12px 11px;
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 4px;
}

/* Name */
.p-name {
    font-size: 13.5px;
    font-weight: 600;
    color: #1a1a2e;
    line-height: 1.45;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 39px;
}

/* Rating */
.p-rating {
    display: flex;
    align-items: center;
    gap: 3px;
}
.r-val {
    font-size: 11.5px;
    font-weight: 700;
    color: #d97706;
}
.r-cnt {
    font-size: 11px;
    color: #b0b0b0;
}

.grow { flex: 1; min-height: 4px; }

/* Price row */
.price-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 2px;
}
.p-price {
    font-size: 17px;
    font-weight: 800;
    color: #F4811F;
    letter-spacing: -0.3px;
}
.p-views {
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    color: #c4c4c4;
}

/* Location */
.p-location {
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    color: #c4c4c4;
    margin-top: 1px;
}
</style>
